/* tslint:disable */
import React, { useEffect, useState, useContext } from 'react';
import { v4 as uuid4 } from 'uuid';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { useApolloClient } from '@apollo/client';
import useProfile, { Profile, ChildNewModel } from '@/hooks/useProfile';
import {
  parentMessages as MESSAGES,
} from './AccountInformation.constants';
import { convertToDescriptionApi } from '@/utils/convertToDescriptionApi';

import {
  GradeChangeInput,
  InstitutionFromSearch,
  ChildGroupByCommunity,
  ChildrenGroupByDistrict,
  PendingSchools,
} from './AccountInformation.types';
import { AccountInformation } from './AccountInformation';
import { SEARCH_FOR_MY_SCHOOL } from '@/graphql/queries';
import {
  AcademicGroupsResponse,
  ManageDistrictAccountResponse,
  UpdateChildrenNewModelResponse
} from '@/graphql/types';
import { ApolloClientName } from '@/config/apolloClient';

import { DISMISS_PENDING_NOTIFICATION, MANAGE_DISTRICT_ACCOUNT, UPDATE_CHILDREN_NEW_MODEL } from '@/graphql/mutation';
import { useSnackbar } from '@/design-system';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import SpinnerOverlay from '@/components/SpinnerOverlay/SpinnerOverlay';
import { useLazySubscriptions } from '@/hooks';

export const AccountInformationContainer: React.FunctionComponent = () => {
  const {
    userId: _userId,
    loading: loadingProfile,
    email,
    managedBy,
    setManagedBy,
    hierarchy,
    revertBlocked: _revertBlocked,
    children: _children,
    updateChild: _updateChild,
    removeChild: _removeChild,
    updateEmail: updateProfileEmail,
    getActiveChildren,
    getPendingChildren,
  } = useProfile() as Profile;
  const showToast = useSnackbar();
  const { notificationPreferences } = useContext(UserPreferencesContext);
  const [useSubscriptionsQuery] = useLazySubscriptions()
  const getSchoolYear = () => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;

    return `${currentYear}-${nextYear}`;
  };
  const [userId, setUserId] = useState(_userId);
  const [dismissible, setDismissable] = useState<boolean>(true);
  const [dismissLocation, setDismissLocation] = useState<string>('');
  const [children, setChildren] = useState(_children);
  const [childrenGrouped, setChildrenGrouped] = useState<
    Array<ChildGroupByCommunity>
  >([]);
  const [pendingSchools, setPendingSchools] = useState<Array<PendingSchools>>(
    [],
  );
  const [childrenAddingIds, setChildrenAddingIds] = useState<Array<string>>([]);
  const [hierarchyType, setHierarchyType] = useState<string | null>(null);
  const [revertBlocked, setRevertBlocked] = useState<boolean>(_revertBlocked);
  const [loading, setLoading] = useState<boolean>(false);
  const isNotificationEnabled = notificationPreferences.newFlyer === true;
  const client = useApolloClient()

  useEffect(() => {
    setRevertBlocked(_revertBlocked);
  }, [_revertBlocked]);

  useEffect(() => {
    setUserId(_userId);
  }, [_userId]);

  useEffect(() => {
    if (!children && _children) {
      const pendingChildren = getPendingChildren();
      const activeChildren = getActiveChildren();
      setChildren(activeChildren);
      setChildrenGrouped(getChildrenGroupedByCommunity(activeChildren));
      if (pendingChildren.length > 0) {
        setPendingSchools(getPendingSchools(pendingChildren));
      }
    } else if (children) {
      setChildrenGrouped(getChildrenGroupedByCommunity(children));
    }
  }, [_children]);

  useEffect(() => {
    setHierarchyType(hierarchy ? hierarchy!.type : null);
  }, [hierarchy]);

  const handleGradeChange = (input: GradeChangeInput, childUId: string) => {
    const updatedChild = {
      wasChanged: true,
      gradeLevel: input.description,
    };
    updateChild(childUId, updatedChild);
  };

  const updateChild = (childUid: string, changes: object) => {
    const index = children!.findIndex(c => c.childUid === childUid);
    const updatedChild: ChildNewModel = {
      ...children![index],
      ...changes,
    };
    if (children) {
      const newChildren = [
        ...children.slice(0, index),
        updatedChild,
        ...children.slice(index + 1),
      ];

      setChildren(newChildren);
    }
  };

  const handleSchoolChange = async (
    value: InstitutionFromSearch & { schoolId: number; districtId: number },
    id: number,
  ) => {
    if (id && value) {
      const gradeLevels: string[] | null = get(value, 'gradeLevels', null);

      const updatedChild = {
        institutionId: value.schoolId.toString(),
        institutionType: 'school',
        institutionName: value.description,
        hierarchyId: value.districtId.toString(),
        hierarchyName: value.districtName.toLocaleLowerCase(),
        hierarchyType: 'district',
        grades: gradeLevels
          ? gradeLevels.map(grade => convertToDescriptionApi(grade))
          : null,
        gradeLevel: 'N',
        wasChanged: true,
        community: value.city,
        postalCode: value.postalCode,
      };

      updateChild(id.toString(), updatedChild);
    } else if (value === null) {
      const index = children!.findIndex(c => c.childUid === id.toString());
      if (children![index].institutionName !== '') {
        updateChild(id.toString(), {
          institutionName: '',
          institutionId: null,
          wasChanged: true,
        });
      }
    }
  };

  const lookUpSchools = async (options, callback) => {
    try {
      const searchTerms = `parentRole-${options.input}` || 'San Diego';

      const { data } = await client.query<AcademicGroupsResponse>({
        query: SEARCH_FOR_MY_SCHOOL,
        context: {
          clientName: ApolloClientName.AccountsBff
        },
        variables: {
          searchTerms,
        }
      })

    const results = data.searchAcademicGroups.schools.reduce(
      // @ts-ignore
      (accum, curr) => [...accum, { ...curr, description: curr.schoolName }],
      [],
    );
    callback(results);
    } catch (e) {
      // tslint:disable:no-console
      console.log('Unable to fetch school names', e);
    }
  };

  const handleClosePage = () => {
    window.location.href = dismissLocation;
  };

  const handleCancel = (childUId: string) => {
    const oldChild = _children!.find(c => c.childUid === childUId)!;
    const cancelChild = {
      ...oldChild,
      wasChanged: false,
    };
    updateChild(childUId, cancelChild);
  };

  const updateRevertBlock = () => {
    if (!revertBlocked) {
      setRevertBlocked(true);
    }
  };

  const handleSubmit = async (childUId: string) => {
    const child = children!.find(c => c.childUid === childUId)!;
    child.status = 'active';
    const newModel = { ...child };
    // @ts-ignore
    delete newModel.__typename;
    delete newModel.grades;
    delete newModel.wasAdded;
    delete newModel.wasChanged;
    // @ts-ignore
    delete newModel.institutionGradeLevel;
    const updateInput = {
      guardianId: userId!,
      add: child.wasAdded ? [newModel] : [],
      change: !child.wasAdded ? [newModel] : [],
    };
    const errorMessage = child.wasAdded
      ? MESSAGES.addChildError
      : MESSAGES.updateChildError;
    try {
      setLoading(true);
      await client.mutate<UpdateChildrenNewModelResponse>({
        mutation: UPDATE_CHILDREN_NEW_MODEL,
        context: {
          clientName: ApolloClientName.AccountsBff
        },
        variables: {
          input:updateInput
        }
      })

      const message = child.wasAdded
        ? MESSAGES.addChildSuccess
        : MESSAGES.updateChildSuccess;
      showToast(message);

      const updatedChild = {
        ...child,
        wasAdded: false,
        wasChanged: false,
      };
      if (child.wasAdded) {
        removeChildrenAddingId(childUId);
        updateChild(childUId, {
          ...child,
          wasAdded: false,
          wasChanged: false,
        });
        _updateChild(childUId, child);
      } else {
        updateChild(childUId, updatedChild);
        _updateChild(childUId, updatedChild);
      }
      updateRevertBlock();
      await useSubscriptionsQuery()  
    } catch (e) {
      showToast(errorMessage);
    }
    finally {
      setLoading(false);
    }
  };

  const handleAddChild = () => {
    const childUid = uuid4();
    setChildren([
      ...children!,
      {
        childUid,
        institutionType: 'school',
        hierarchyType: 'district',
        institutionId: '',
        institutionName: '',
        hierarchyId: '',
        hierarchyName: '',
        gradeLevel: 'N',
        schoolYear: getSchoolYear(),
        schoolType: 'peachjar-school',
        community: null,
        postalCode: '',
        status: 'active',
        institutionGradeLevel: '',
        wasAdded: true,
        wasChanged: false,
      },
    ]);

    setChildrenAddingIds([...childrenAddingIds, childUid]);
  };

  const handleRemoveChild = async (childUId: string) => {
    if (children) {
      const child = children.find(c => c.childUid === childUId)!;
      const { grades, institutionGradeLevel, wasAdded, wasChanged, ...rest } =
        child;
      const deleteChild = {
        ...rest,
        status: 'removed',
      };
      // @ts-ignore
      delete deleteChild.__typename;
      try {
        setLoading(true);
        const {
          data
        } = await client.mutate<UpdateChildrenNewModelResponse>({
          context: {
            clientName: ApolloClientName.AccountsBff
          },
          mutation: UPDATE_CHILDREN_NEW_MODEL,
          variables: {
            input: {
              guardianId: userId!,
              change: [deleteChild],
            }
          }
        });

        if (data?.updateChildrenNewModel) {
          showToast(MESSAGES.removeChildSuccess)
          removeChild(childUId);
          _removeChild(childUId);
          await useSubscriptionsQuery()
          updateRevertBlock();
        } else {
          showToast(MESSAGES.removeChildError)
        }
      } catch (err) {
        showToast(MESSAGES.removeChildError)
      }
      finally {
        setLoading(false);
      }
    }
  };

  const handleRemoveAddedChild = (childUId: string) => {
    removeChildrenAddingId(childUId);
    setChildren(children!.filter(c => c.childUid !== childUId));
  };

  const removeChildrenAddingId = (childUId: string) => {
    const newChildrenAddingIds = childrenAddingIds.filter(c => c !== childUId);
    setChildrenAddingIds(newChildrenAddingIds);
  };

  const removeChild = (childUid: string) => {
    setChildren(children!.filter(c => c.childUid !== childUid));
  };

  const changeManagedByStatus = async () => {
    try {
      setLoading(true);
      const newManageBy = managedBy === 'sis' ? 'family' : 'sis';
      const {
        data
      } = await client.mutate<ManageDistrictAccountResponse>({
        context: {
          clientName: ApolloClientName.AccountsBff
        },
        mutation: MANAGE_DISTRICT_ACCOUNT,
        variables: {
          input: {
            guardianId: userId,
            managedBy: newManageBy
          },
        }
      })
      if (data?.changeManageStatus.managedBy) {
        setManagedBy(data?.changeManageStatus.managedBy);
        return;
      }
      console.log('Unable to change account management status: ');
      showToast(MESSAGES.changeManageByError)
    } catch (e) {
      console.log('Unable to change account management status: ', e);
      showToast(MESSAGES.changeManageByError)
    }
    finally {
      setLoading(false);
    }
  };

  const sortChildrenByGradesAsc = (
    children: ChildNewModel[],
  ): Array<ChildNewModel> =>
    children.sort((a, b) => {
      if (a.institutionName !== b.institutionName) {
        // @ts-ignore
        return a.institutionName - b.institutionName;
      }
      // @ts-ignore
      return a.gradeLevel - b.gradeLevel;
    });

  const getPendingSchools = (
    children: ChildNewModel[],
  ): Array<PendingSchools> => {
    const pendingSchoolsList: Array<PendingSchools> = [];
    const districts = sortBy([...new Set(children.map(c => c.hierarchyName))]);
    districts.forEach(districtName => {
      const schools = sortBy(
        children.filter(c => c.hierarchyName === districtName),
        c => c.institutionName,
      ).map(({ institutionId, institutionName, gradeLevel }) => ({
        institutionName,
        gradeLevel,
        isNewSchool: !_children!.find(
          c => c.institutionId === institutionId && c.status === 'active',
        ),
      }));
      pendingSchoolsList.push({
        districtName,
        schools,
      });
    });
    return pendingSchoolsList;
  };

  const getChildrenGroupedByCommunity = (
    children: ChildNewModel[] | null,
  ): Array<ChildGroupByCommunity> => {
    const linkedChildGroupByCommunity: Array<ChildGroupByCommunity> = [];
    if (children) {
      let childrenGroupedByDistrict: Array<ChildrenGroupByDistrict> = [];
      let childrenByDistrict: ChildrenGroupByDistrict;
      const communites = sortBy([
        ...new Set(children.map(item => item.community)),
      ]);
      communites.forEach(community => {
        childrenGroupedByDistrict = [];
        const childrenList = sortBy(
          children.filter(l => l.community === community),
          c => c.institutionName,
        );
        const districts = sortBy([
          ...new Set(childrenList.map(item => item.hierarchyName)),
        ]);
        districts.forEach(hierarchyName => {
          childrenByDistrict = {
            district: hierarchyName,
            childrenIds: sortChildrenByGradesAsc(
              childrenList.filter(c => c.hierarchyName === hierarchyName),
            ).map(c => c.childUid),
          };
          childrenGroupedByDistrict.push(childrenByDistrict);
        });

        linkedChildGroupByCommunity.push({
          community,
          districts: childrenGroupedByDistrict,
        });
      });
    }
    return linkedChildGroupByCommunity;
  };

  const handleDismissPendingNotification = async (guardianId: number) => {
    try {
      setLoading(true);
      await client.mutate({
        context: {
          clientName: ApolloClientName.AccountsBff
        },
        mutation: DISMISS_PENDING_NOTIFICATION,
        variables: {
          guardianId,
        }
      });
      setPendingSchools([]);
    }catch(err){
      console.log("err=>", err);
    }
    finally {
      setLoading(false);
    }

  };

  const accountManagementEnabled = managedBy !== 'sis';

  const mergedProps = {
    userId,
    email,
    managedBy,
    childrenGrouped,
    pendingSchools,
    childrenList: children,
    childrenAddingIds,
    dismissible,
    accountManagementEnabled,
    isNotificationEnabled,
    revertBlocked,
    updateRevertBlock,
    changeManagedByStatus,
    lookUpSchools,
    handleSubmit,
    handleSchoolChange,
    handleGradeChange,
    handleRemoveChild,
    handleRemoveAddedChild,
    handleAddChild,
    handleClosePage,
    handleCancel,
    handleDismissPendingNotification,
  };

  // if (hierarchyType !== 'family' && hierarchyType !== 'parent') {
  //   return <ManageNotifications />;
  // }

  return (
    <>
      <SpinnerOverlay loading={loading || loadingProfile} />
      {!loadingProfile && <AccountInformation {...mergedProps} />}
    </>
  );
};
